"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { GraduationCap, Microscope, FileCheck2, ArrowRight, Sparkles, ShieldCheck, FlaskConical, BookOpen, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const keyPillars = [{
  title: "Method Development",
  desc: "Analytical workflows tailored for university labs and research grant milestones.",
  icon: FlaskConical
}, {
  title: "Regulatory Compliance",
  desc: "GLP/GMP aligned qualifications and instrument maintenance support.",
  icon: ShieldCheck
}, {
  title: "Grant Application Support",
  desc: "Instrument specifications, validation protocols, and lifecycle budget planning.",
  icon: BookOpen
}];
const stats = [{
  value: "25+",
  label: "Years Supporting Academic Labs"
}, {
  label: "Traceable Calibration & IQ/OQ Support"
}, {
  label: "On-Site Technical Response"
}];
export default function ResearchHero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    initial: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="academicresearchhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(ellipse_at_top_right,var(--color-accent),transparent_70%)]" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Core Messaging */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            {/* Breadcrumb */}
            <motion.nav {...fadeIn} aria-label="breadcrumb">
              <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-foreground">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" className="[&>svg]:size-3.5">
                  <ChevronRight className="size-3.5 text-muted-foreground" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/IndustriesPage" className="transition-colors hover:text-foreground">
                    Industries
                  </Link>
                </li>
                <li aria-hidden="true" className="[&>svg]:size-3.5">
                  <ChevronRight className="size-3.5 text-muted-foreground" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span role="link" aria-disabled="true" aria-current="page" className="font-medium text-foreground">
                    Academic and Research
                  </span>
                </li>
              </ol>
            </motion.nav>

            <motion.div {...fadeIn} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs gap-1.5 font-medium border border-border">
                <GraduationCap className="size-3.5 text-primary" />
                <span>Academic & Research Excellence</span>
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-xs gap-1.5 font-medium">
                <Sparkles className="size-3 text-primary" />
                <span>Ireland & UK Instrumentation</span>
              </Badge>
            </motion.div>

            <motion.h1 {...fadeIn} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: "easeOut"
          }} className="font-default text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.12]">
              Advanced Analytical Systems for Scientific Discovery
            </motion.h1>

            <motion.p {...fadeIn} transition={{
            duration: 0.5,
            delay: 0.2,
            ease: "easeOut"
          }} className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              We equip university research facilities, higher education laboratories, and research institutes across Ireland and the UK with application-led analytical technology, qualification, and complete lifecycle support.
            </motion.p>

            {/* Action Buttons */}
            <motion.div {...fadeIn} transition={{
            duration: 0.5,
            delay: 0.3,
            ease: "easeOut"
          }} className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="shadow-sm font-medium">
                <Link to="/SolutionsPage">
                  <span>Explore Research Solutions</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium">
                <Link to="/ContactPage">
                  <span>Speak to an Application Specialist</span>
                </Link>
              </Button>
            </motion.div>

            {/* Metrics */}
            <motion.div {...fadeIn} transition={{
            duration: 0.5,
            delay: 0.4,
            ease: "easeOut"
          }} className="grid grid-cols-3 gap-4 pt-6 border-t border-border mt-4">
              {stats.map((stat, i) => <div data-index={i} key={stat.label} className="flex flex-col justify-end">
                  {stat.value && <span className="font-default text-2xl sm:text-3xl font-bold text-foreground">
                      {stat.value}
                    </span>}
                  <span className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                    {stat.label}
                  </span>
                </div>)}
            </motion.div>
          </div>

          {/* Right Column: Visual Imagery and Highlight Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-4">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-md bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/academic_research_laboratory_spectroscopy.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/academic_research_laboratory_spectroscopy.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/academic_research_laboratory_spectroscopy.png-wvc-sizes" alt="Analytical laboratory equipment and researchers working in academic facility" loading="lazy" decoding="async" className="w-full h-[320px] sm:h-[380px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent flex items-end p-6">
                <div className="text-background">
                  <p className="text-xs font-semibold uppercase tracking-wider text-background/80">
                    Trusted Laboratory Infrastructure
                  </p>
                  <p className="text-sm sm:text-base font-medium mt-1">
                    Delivering precision measurements, verifiable workflows, and compliant data systems.
                  </p>
                </div>
              </div>
            </div>

            {/* Pillar feature box */}
            <div className="grid grid-cols-1 gap-3">
              {keyPillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              return <Card key={pillar.title} data-index={i} className="py-3 px-4 border border-border shadow-xs bg-card">
                    <CardContent className="p-0 flex items-start gap-3.5">
                      <div className="p-2 rounded-lg bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                        <IconComponent className="size-4 text-primary" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-foreground">
                          {pillar.title}
                        </span>
                        <span className="text-xs text-muted-foreground leading-normal mt-0.5">
                          {pillar.desc}
                        </span>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}